import { Controller } from "@hotwired/stimulus"

// checkout-otp controller
// Handles the 6-digit OTP input on the checkout verifying step.
//
// Values (passed from ERB via data-checkout-otp-*-value):
//   sentAt  [String]  ISO timestamp of when the OTP was dispatched (otp_sent_at).
//                     Used to calculate the correct remaining countdown instead of
//                     always resetting to 5:00 on every page load.
//
// Targets:
//   digitInput   — the 6 individual character boxes
//   hiddenInput  — assembles the full code for form submission
//   submitButton — disabled until all 6 digits are filled
//   inlineError  — small shake-error message below inputs
//   resendTimer  — spinning countdown row
//   resendLink   — "Resend Code" row (shown after timer expires)
//   timerText    — the m:ss display inside resendTimer

export default class extends Controller {
  static targets = [
    "digitInput",
    "hiddenInput",
    "submitButton",
    "inlineError",
    "resendTimer",
    "resendLink",
    "timerText",
  ]

  static values = {
    sentAt: String, // ISO string from session[:checkout]["otp_sent_at"]
    ttlSeconds: Number, // OTP TTL in seconds from UserAccount.email_otp_expiry
  }

  // ── Lifecycle ────────────────────────────────────────────────────────

  connect() {
    this._countdownId = null

    // Focus first empty box on mount
    setTimeout(() => this._focusFirst(), 80)

    this._startCountdown()
  }

  disconnect() {
    this._stopCountdown()
  }

  // ── Input handlers ───────────────────────────────────────────────────

  handleInput(event) {
    const input = event.currentTarget
    const idx   = parseInt(input.dataset.index, 10)

    // Keep only first digit
    const digit = input.value.replace(/\D/g, "").slice(0, 1)
    input.value = digit

    this._clearInputError()

    if (digit && idx < 5) {
      this.digitInputTargets[idx + 1].focus()
    }

    this._sync()
  }

  handleKeydown(event) {
    const input = event.currentTarget
    const idx   = parseInt(input.dataset.index, 10)

    switch (event.key) {
      case "Backspace":
        event.preventDefault()
        if (input.value) {
          input.value = ""
        } else if (idx > 0) {
          const prev = this.digitInputTargets[idx - 1]
          prev.value = ""
          prev.focus()
        }
        this._sync()
        break
      case "ArrowLeft":
        if (idx > 0) this.digitInputTargets[idx - 1].focus()
        break
      case "ArrowRight":
        if (idx < 5) this.digitInputTargets[idx + 1].focus()
        break
    }
  }

  handlePaste(event) {
    event.preventDefault()
    const raw    = (event.clipboardData || window.clipboardData).getData("text")
    const digits = raw.replace(/\D/g, "").slice(0, 6)
    if (!digits.length) return

    this.digitInputTargets.forEach((box, i) => {
      box.value = digits[i] || ""
    })

    // Focus first empty or last box
    const focusIdx = Math.min(digits.length, 5)
    this.digitInputTargets[focusIdx].focus()

    this._sync()
  }

  // ── Private helpers ──────────────────────────────────────────────────

  // Assembles the 6-digit code into the hidden input and toggles submit.
  _sync() {
    const code = this.digitInputTargets.map(b => b.value).join("")
    this.hiddenInputTarget.value = code

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = code.length !== 6
    }
  }

  _focusFirst() {
    const first = this.digitInputTargets.find(b => !b.value)
    ;(first || this.digitInputTargets[0])?.focus()
  }

  // Show inline shake error
  _showInputError(msg) {
    if (!this.hasInlineErrorTarget) return
    this.inlineErrorTarget.textContent = msg
    this.inlineErrorTarget.classList.remove("hidden")

    // Shake digits
    this.digitInputTargets.forEach(b => b.classList.add("border-red-400", "bg-red-50"))
    setTimeout(() => {
      this.digitInputTargets.forEach(b => {
        b.classList.remove("border-red-400", "bg-red-50")
        b.value = ""
      })
      this._focusFirst()
      this._sync()
    }, 600)
  }

  _clearInputError() {
    if (!this.hasInlineErrorTarget) return
    this.inlineErrorTarget.classList.add("hidden")
    this.inlineErrorTarget.textContent = ""
  }

  // ── Countdown timer ──────────────────────────────────────────────────

  _startCountdown() {
    this._stopCountdown()

    let remaining = this.ttlSecondsValue;

    if (this.sentAtValue) {
      const sentAt  = new Date(this.sentAtValue)
      const elapsed = Math.floor((Date.now() - sentAt.getTime()) / 1000)
      remaining = Math.max(0, this.ttlSecondsValue - elapsed);
    }

    // Already expired — show resend immediately, no countdown
    if (remaining <= 0) {
      this._showResend()
      return
    }

    this._updateTimerText(remaining)

    this._countdownId = setInterval(() => {
      remaining -= 1
      this._updateTimerText(remaining)

      if (remaining <= 0) {
        this._stopCountdown()
        this._showResend()
      }
    }, 1000)
  }

  _stopCountdown() {
    if (this._countdownId) {
      clearInterval(this._countdownId)
      this._countdownId = null
    }
  }

  _showResend() {
    if (this.hasResendTimerTarget) this.resendTimerTarget.classList.add("hidden")
    if (this.hasResendLinkTarget)  this.resendLinkTarget.classList.remove("hidden")
  }

  _updateTimerText(seconds) {
    if (!this.hasTimerTextTarget) return
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    this.timerTextTarget.textContent = `${m}:${String(s).padStart(2, "0")}`
  }
}
